import React from 'react';
import { Mail, MapPin, Phone, Share2, MessageCircle } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';

const ContattiPage = () => {
  const { settings } = useSettings();

  const getSocialIcon = (platform) => {
    const size = 24;
    switch (platform) {
      case 'instagram':
      case 'facebook':
      case 'tiktok':
      case 'youtube':
        return <Share2 size={size} className="text-brand-dark" />;
      case 'whatsapp':
      case 'telegram':
        return <MessageCircle size={size} className="text-brand-dark" />;
      default:
        return <Mail size={size} className="text-brand-dark" />;
    }
  };

  const renderSocialCard = (platform, url, label) => {
    if (!url) return null;

    const isWhatsApp = platform === 'whatsapp' && url.match(/^\+?[0-9]/);
    const href = isWhatsApp ? `https://wa.me/${url.replace(/\D/g, '')}` : url;

    return (
      <div key={platform} className="bg-white p-8 rounded-lg border border-brand-main/20 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-brand-main/20 rounded-lg">
            {getSocialIcon(platform)}
          </div>
          <h3 className="font-display text-xl italic text-brand-dark capitalize">{platform}</h3>
        </div>
        <p className="font-body text-brand-muted">
          {label}
        </p>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-dark font-medium hover:text-brand-main transition-colors mt-2 block"
        >
          {url}
        </a>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-brand-light pt-20 md:pt-24">
      {/* Masthead */}
      <div className="py-16 md:py-24 px-4 md:px-6 lg:px-8 border-b border-brand-main/20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-display text-6xl md:text-7xl italic font-light text-brand-ink mb-4">
            Contatti
          </h1>
          <p className="font-body text-lg text-brand-muted">
            Vuoi collaborare o hai domande? Sarò felice di ascoltarti.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-20 md:py-28 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Contact Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Email */}
            {settings.contact_email && (
              <div className="bg-white p-8 rounded-lg border border-brand-main/20 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-brand-main/20 rounded-lg">
                    <Mail size={24} className="text-brand-dark" />
                  </div>
                  <h3 className="font-display text-xl italic text-brand-dark">Email</h3>
                </div>
                <p className="font-body text-brand-muted">
                  Per consultazioni, commissioni e domande generali.
                </p>
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="text-brand-dark font-medium hover:text-brand-main transition-colors mt-2 block"
                >
                  {settings.contact_email}
                </a>
              </div>
            )}

            {/* Telefono */}
            {settings.contact_phone && (
              <div className="bg-white p-8 rounded-lg border border-brand-main/20 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-brand-main/20 rounded-lg">
                    <Phone size={24} className="text-brand-dark" />
                  </div>
                  <h3 className="font-display text-xl italic text-brand-dark">Telefono</h3>
                </div>
                <p className="font-body text-brand-muted">
                  Puoi contattarmi direttamente al numero.
                </p>
                <a
                  href={`tel:${settings.contact_phone}`}
                  className="text-brand-dark font-medium hover:text-brand-main transition-colors mt-2 block"
                >
                  {settings.contact_phone}
                </a>
              </div>
            )}

            {/* Location */}
            {settings.contact_address && (
              <div className="bg-white p-8 rounded-lg border border-brand-main/20 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-brand-main/20 rounded-lg">
                    <MapPin size={24} className="text-brand-dark" />
                  </div>
                  <h3 className="font-display text-xl italic text-brand-dark">Ubicazione</h3>
                </div>
                <p className="font-body text-brand-muted">
                  Basato in:
                </p>
                <p className="text-brand-dark font-medium mt-2">{settings.contact_address}</p>
              </div>
            )}

            {/* Social Media Dinamici */}
            {settings.social_instagram && renderSocialCard('instagram', settings.social_instagram, 'Segui le novità e gli ultimi lavori')}
            {settings.social_facebook && renderSocialCard('facebook', settings.social_facebook, 'Seguimi su Facebook')}
            {settings.social_whatsapp && renderSocialCard('whatsapp', settings.social_whatsapp, 'Contattami via WhatsApp')}
            {settings.social_telegram && renderSocialCard('telegram', settings.social_telegram, 'Contattami su Telegram')}
          </div>

          {/* Main CTA */}
          <div className="bg-brand-dark text-white rounded-lg p-12 md:p-16 text-center">
            <h2 className="font-display text-3xl md:text-4xl italic font-light mb-4">
              {settings.contact_cta_title}
            </h2>
            <p className="font-body text-brand-light/80 mb-8 text-lg max-w-2xl mx-auto">
              {settings.contact_cta_text}
            </p>
            {settings.contact_email && (
              <a
                href={`mailto:${settings.contact_email}`}
                className="inline-block bg-white text-brand-dark px-8 py-3 rounded-full font-medium hover:bg-brand-light transition-colors"
              >
                Scrivi una email
              </a>
            )}
          </div>

          {/* Response time notice */}
          <div className="mt-12 text-center">
            <p className="font-body text-sm text-brand-muted">
              Rispondo di solito entro 24-48 ore. Grazie per la tua pazienza!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContattiPage;
